import { useMemo } from "react";
import { useLocation } from "react-router-dom";

import { adminNavigationConfig } from "./admin.config";

/**
 * 面包屑展示项。
 */
export interface AdminBreadcrumbItem {
  label: string;
}

/**
 * 后台导航解析结果。
 */
export interface AdminNavigationResolved {
  selectedKey: string;
  pageTitle: string;
  pageDescription: string;
  breadcrumbs: AdminBreadcrumbItem[];
}

const defaultResolvedNavigation: AdminNavigationResolved = {
  selectedKey: "workspace",
  pageTitle: "工作台",
  pageDescription: "管理经营指标、业务动态和待处理事项。",
  breadcrumbs: [{ label: "首页" }, { label: "工作台" }],
};

const notificationCenterResolvedNavigation: AdminNavigationResolved = {
  selectedKey: "notification-center",
  pageTitle: "通知公告",
  pageDescription: "查看普通通知和仍需进入业务页面处理的站内消息。",
  breadcrumbs: [{ label: "首页" }, { label: "通知公告" }],
};

const customizationWorkbenchResolvedNavigation: AdminNavigationResolved = {
  selectedKey: "customization-projects",
  pageTitle: "工作台",
  pageDescription: "基于项目追溯记录生成、查看和维护定制需求版本。",
  breadcrumbs: [{ label: "首页" }, { label: "CRM 系统" }, { label: "定制列表" }, { label: "工作台" }],
};

const quoteBudgetResolvedNavigation: AdminNavigationResolved = {
  selectedKey: "crm",
  pageTitle: "行程核价",
  pageDescription: "核定行程成本、人数与资源明细，形成可追溯的定稿版本。",
  breadcrumbs: [{ label: "首页" }, { label: "CRM 系统" }, { label: "行程核价" }],
};

const customerQuoteResolvedNavigation: AdminNavigationResolved = {
  selectedKey: "crm",
  pageTitle: "行程报价",
  pageDescription: "基于定稿核价维护多版本对客报价、表达模板与成交金额。",
  breadcrumbs: [{ label: "首页" }, { label: "CRM 系统" }, { label: "行程报价" }],
};

const orderHandoverDetailResolvedNavigation: AdminNavigationResolved = {
  selectedKey: "customization-projects",
  pageTitle: "合同交接",
  pageDescription: "维护合同交接信息、出行名单与合同挂网签署。",
  breadcrumbs: [
    { label: "首页" },
    { label: "CRM 系统" },
    { label: "定制列表" },
    { label: "合同交接" },
  ],
};

const fulfillmentOrderEditorResolvedNavigation: AdminNavigationResolved = {
  selectedKey: "fulfillment-orders",
  pageTitle: "确认件工作区",
  pageDescription: "按供应商结算主体维护确认行草稿，并显式保存和生成协议。",
  breadcrumbs: [
    { label: "首页" },
    { label: "供应链管理系统" },
    { label: "确认件" },
    { label: "工作区" },
  ],
};

/** 解析需要独立标签标题的定制工作台路由。 */
function resolveCustomizationWorkbenchNavigation(
  pathname: string,
  search = "",
): AdminNavigationResolved {
  const projectId =
    pathname.match(/^\/crm\/customization\/projects\/([^/]+)\/workbench/)?.[1] ??
    pathname.match(
      /^\/crm\/opportunities\/[^/]+\/customization-projects\/([^/]+)\/workbench/,
    )?.[1] ??
    "";
  const query = search.startsWith("?") ? search.slice(1) : search;
  const jiDiaoNo = new URLSearchParams(query).get("JiDiaoNo")?.trim() ?? "";
  if (projectId === "new") {
    return { ...customizationWorkbenchResolvedNavigation, pageTitle: "新建定制" };
  }
  if (jiDiaoNo) {
    return { ...customizationWorkbenchResolvedNavigation, pageTitle: jiDiaoNo };
  }
  return customizationWorkbenchResolvedNavigation;
}

/**
 * 根据路径解析后台导航信息。
 */
export function resolveAdminNavigation(pathname: string, search = ""): AdminNavigationResolved {
  if (/^\/ticket-refunds\/teams\/[^/]+\/?$/.test(pathname)) {
    return {
      selectedKey: "course-routes",
      pageTitle: "团退票申请",
      pageDescription: "查看退票凭证、申请资料并完成人工审核。",
      breadcrumbs: [{ label: "首页" }, { label: "课程线路管理" }, { label: "团退票申请" }],
    };
  }
  if (pathname === "/notifications/inbox") {
    return notificationCenterResolvedNavigation;
  }
  if (pathname === "/crm/customization/projects/opening") {
    return {
      ...customizationWorkbenchResolvedNavigation,
      pageTitle: "正在创建定制",
      pageDescription: "正在创建定制项目，完成后将自动进入工作台。",
      breadcrumbs: [
        { label: "首页" },
        { label: "CRM" },
        { label: "定制项目" },
        { label: "正在创建定制" },
      ],
    };
  }
  if (isCustomizationDemandExcelDetailPath(pathname)) {
    return {
      selectedKey: "customization-projects",
      pageTitle: "全部需求版本",
      pageDescription: "只读查看定制需求版本的 Excel 详表。",
      breadcrumbs: [
        { label: "首页" },
        { label: "CRM" },
        { label: "定制列表" },
        { label: "全部需求版本" },
      ],
    };
  }
  if (isCustomizationWorkbenchPath(pathname)) {
    return resolveCustomizationWorkbenchNavigation(pathname, search);
  }
  if (/^\/quote\/budgets\/[^/]+$/.test(pathname)) {
    return quoteBudgetResolvedNavigation;
  }
  if (/^\/quote\/customer-quotes\/[^/]+$/.test(pathname)) {
    return customerQuoteResolvedNavigation;
  }
  if (isOrderHandoverDetailPath(pathname)) {
    return orderHandoverDetailResolvedNavigation;
  }
  if (/^\/crm\/order-handover\/[^/]+\/rosters\/[^/]+\/members\/[^/]+\/fee-adjustments\/?$/.test(pathname)) {
    return {
      ...orderHandoverDetailResolvedNavigation,
      pageTitle: "成员费用调整详情",
      pageDescription: "查看成员费用调整记录并维护加价总金额。",
      breadcrumbs: [...orderHandoverDetailResolvedNavigation.breadcrumbs, { label: "成员费用调整详情" }],
    };
  }
  if (/^\/supply-chain\/fulfillment-orders\/workspaces\/[^/]+$/.test(pathname)) {
    return fulfillmentOrderEditorResolvedNavigation;
  }
  if (/^\/supply-chain\/fulfillment-orders\/itineraries\/[^/]+(?:\/enter)?$/.test(pathname)) {
    return fulfillmentOrderEditorResolvedNavigation;
  }

  const matchedItem =
    adminNavigationConfig.find((item) => pathname === item.path) ??
    [...adminNavigationConfig]
      .sort((left, right) => right.path.length - left.path.length)
      .find(
        (item) =>
          item.path !== "/workspace" &&
          (pathname === item.path || pathname.startsWith(`${item.path}/`)),
      ) ??
    undefined;

  if (!matchedItem) {
    return defaultResolvedNavigation;
  }

  return {
    selectedKey: matchedItem.key,
    pageTitle: matchedItem.pageTitle,
    pageDescription: matchedItem.pageDescription,
    breadcrumbs: matchedItem.breadcrumb.map((label) => ({ label })),
  };
}

/** 判断是否为任意项目的定制工作台路径（含商机关联定制入口）。 */
function isCustomizationWorkbenchPath(pathname: string): boolean {
  return (
    /^\/crm\/customization\/projects\/[^/]+\/workbench(?:\/.*)?$/.test(pathname) ||
    /^\/crm\/opportunities\/[^/]+\/customization-projects\/[^/]+\/workbench(?:\/.*)?$/.test(pathname)
  );
}

/** 判断是否为需求 Excel 详表只读落地页路径。 */
function isCustomizationDemandExcelDetailPath(pathname: string): boolean {
  return /^\/crm\/customization\/projects\/[^/]+\/demands\/[^/]+\/excel-detail\/?$/.test(pathname);
}

/** 判断是否为合同交接详情路径（不含名单导入独立页）。 */
function isOrderHandoverDetailPath(pathname: string): boolean {
  return /^\/crm\/order-handover\/[^/]+\/?$/.test(pathname);
}

/**
 * 在后台布局中解析当前导航状态。
 */
export function useAdminNavigation() {
  const location = useLocation();

  return useMemo(
    () => resolveAdminNavigation(location.pathname, location.search),
    [location.pathname, location.search],
  );
}
